import React, { ReactElement, useState, useEffect, useRef } from 'react';
import Head from 'next/head';
import cn from 'classnames';

import { Icon, Link, Spinner } from 'components';
import { getLayout } from 'components/layouts/Sidebar';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import ToolFilters from 'components/ToolFilters';
import { useToolFilters } from 'hooks/useToolFilters';
import { PAGE_ROUTES } from 'lib/page-routes';
import { withAuth } from 'lib/hocs/withAuth';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';
import AttendeeCard from './AttendeeCard';
import styles from './TalentSource.module.scss';
import { MessageButton, FilterButton } from './HeadingActions';
import { TalentScreening } from './ScreeningModeModal/TalentScreening/TalentScreening.component';
import TalentMessageModal from './TalentMessageModal';

export type TalentStatus =
  | 'talent_pool'
  | 'viewed'
  | 'saved'
  | 'click_applied'
  | 'applied';

const TalentSourcing = (): ReactElement => {
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<
    Record<string, Array<string>>
  >({});

  const {
    talentPool,
    filters,
    onChangeFilter,
    isLoading,
    talentMetaInfo,
    hasMoreRecords,
    fetchMoreData,
    isLoadingMore,
    isMultiselectDisabled,
    partialFilters,
    onChangeGroupedFilter,
    partialCount,
    isPartialLoading,
  } = useToolFilters({ transaction: 'talent_pool', activeFilters });

  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const sectionRef = React.useRef<HTMLElement>(null);

  const [selectedApplicantId, setSelectedApplicantId] = useState<
    string | undefined
  >();
  const [headerWidth, setHeaderWidth] = useState<number>(0);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [isOpenTalentScreeningModal, setIsOpenTalentScreeningModal] =
    useState(false);
  const [expandedTalentScreen, setExpandedTalentScreen] = useState<
    TalentStatus | undefined
  >();
  const [formattedValue, setFormattedValue] = useState<string | number>('');

  const allCandidateCountRef = useRef<number | undefined>();
  const onSelectCard = (id: string) => {
    const value = selectedCards.includes(id);
    if (value) {
      setSelectedCards(selectedCards.filter((item) => item !== id));
    } else {
      setSelectedCards([...selectedCards, id]);
    }
  };

  const onIndividualMessage = (
    e: React.MouseEvent<HTMLElement>,
    id: string
  ) => {
    setOpenMessageModal(true);
    e?.stopPropagation();
    e?.preventDefault();

    setSelectedCards([id]);
  };
  useEffect(() => {
    if (sectionRef.current?.offsetWidth !== headerWidth)
      setHeaderWidth(sectionRef.current?.offsetWidth || 0);
  }, [sectionRef.current?.offsetWidth, scrolled]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', () => {
      setHeaderWidth(sectionRef.current?.offsetWidth || 0);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [openFilters]);

  useEffect(() => {
    if (!openFilters && scrolled) {
      const scroll = document.documentElement.scrollTop > 101;
      setScrolled(scroll);
    }
  }, [openFilters]);

  useEffect(() => {
    const newFormattedValue = getFormattedValue(talentMetaInfo?.total);

    if (
      talentMetaInfo?.total !== undefined &&
      talentMetaInfo?.total !== allCandidateCountRef.current
    ) {
      // Update the ref with the new count
      allCandidateCountRef.current = talentMetaInfo?.total;
      // Update the state with the displayed count
      setFormattedValue(newFormattedValue);
    }
  }, [talentMetaInfo]);

  const handleScroll = () => {
    const scroll = document.documentElement.scrollTop > 101;
    if (!scroll && openFilters) {
      setScrolled(true);
    } else {
      setScrolled(scroll);
    }
  };

  function getFormattedValue(value?: number) {
    // If the value is 1000 or above
    if (value) {
      if (value >= 1000) {
        const data = {
          k: Math.floor(value / 1000),
          point: ((value % 1000) / 10).toFixed(0),
        };
        return `${data?.k}.${data?.point}k`;
      } else return value;
    }
    return '';
  }

  const toggleTalentScreeningMode =
    (openScreeningMode: boolean, screeningTalent?: TalentStatus) => () => {
      setExpandedTalentScreen(screeningTalent);
      setIsOpenTalentScreeningModal(openScreeningMode);
      if (!openScreeningMode) {
        setSelectedApplicantId('');
      }
    };

  const handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    e?.preventDefault();
    toggleTalentScreeningMode(true, 'talent_pool' as TalentStatus)();
  };

  return (
    <>
      <Head>
        <title>Talent Sourcing</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section
        className={styles.TalentSourcingPage}
        data-testid="TalentSourcingPage"
        ref={sectionRef}
      >
        {scrolled ? <div className={styles.headerPlaceHolder} /> : null}
        <Row
          align="center"
          justify="space-between"
          className={cn(styles.header, { [styles.headerScrolled]: scrolled })}
          style={{ ...(scrolled && { width: headerWidth }) }}
        >
          <div
            className={cn(styles.headerDesc, {
              [styles.headerDescScrolled]: scrolled,
            })}
          >
            {!scrolled ? (
              <div>
                <Link
                  href={`${PAGE_ROUTES.HIRING}`}
                  underlineOnHover={true}
                  className={styles.link1}
                >
                  <Icon
                    iconName="chevron-left"
                    className={cn(styles.chevronIcon, {
                      [styles.chevronIconScrolled]: scrolled,
                    })}
                  />
                  {!scrolled ? 'Hiring' : ''}
                </Link>
              </div>
            ) : null}
            <Link
              className={cn(styles.title, { [styles.titleScrolled]: true })}
              href={''}
              target="_blank"
              underlineOnHover={true}
            >
              Talent sourcing
            </Link>
            <p className={styles.candidateCount}>
              {!scrolled && formattedValue
                ? `${formattedValue} candidates`
                : ''}
            </p>
          </div>
          <Row className={styles.actionsRow}>
            <MessageButton
              filters={filters || undefined}
              selectedCount={selectedCards?.length}
              onMessageAll={() => {
                setSelectedCards(talentPool.map((obj) => obj?.id));
                setOpenMessageModal(true);
              }}
              onMessageSelectedCandidates={() => setOpenMessageModal(true)}
            />
            <ButtonV3
              color="secondary"
              onClick={() => {
                toggleTalentScreeningMode(
                  true,
                  'talent_pool' as TalentStatus
                )();
              }}
              startIcon="icon_arrows-maximize"
              borderless={!scrolled}
            >
              Screening mode
            </ButtonV3>
            {scrolled ? (
              <FilterButton
                onClick={() => setOpenFilters(!openFilters)}
                isOpen={openFilters}
                borderless={!scrolled}
              />
            ) : null}
          </Row>
        </Row>
        <ToolFilters
          isScroll={scrolled}
          open={scrolled && openFilters ? true : !scrolled ? true : false}
          filters={filters}
          onChangeFilter={onChangeFilter}
          isTalentSource={true}
          isMultiselectDisabled={isMultiselectDisabled}
          className={styles['talent-source-filters']}
          scrollPosition={'48px'}
          partialFilters={partialFilters}
          partialCount={partialCount}
          isPartialLoading={isPartialLoading}
          onChangeGroupedFilter={onChangeGroupedFilter}
        />
        <div className={styles.TalentSourcingPageContent}>
          {isLoading ? (
            <Spinner wrapperClassname={styles.spinner} />
          ) : talentPool?.length === 0 ? (
            <div className={styles['no-users-found']}>No users found.</div>
          ) : (
            <div className={styles.TalentSourcingPageContentCard}>
              {talentPool?.map((item, index) => (
                <AttendeeCard
                  key={index}
                  image={item?.avatar_url}
                  name={item?.name || item?.email}
                  isSideIcon={true}
                  iconName="message"
                  onSideIconClick={(e: React.MouseEvent<HTMLElement>) =>
                    onIndividualMessage(e, item.id)
                  }
                  onCardClick={(e) => {
                    setSelectedApplicantId(item?.id);
                    handleCardClick(e);
                  }}
                  isCheckbox={true}
                  checked={selectedCards?.includes(item?.id)}
                  handleCheckboxChange={() => {
                    onSelectCard(item?.id);
                  }}
                />
              ))}
            </div>
          )}
          {!isLoading && isLoadingMore ? (
            <div className={styles.TalentSourcingPageLoadingMore}>
              <Spinner />
            </div>
          ) : null}
          <div className={styles.TalentSourcingPageFooter}>
            {talentMetaInfo?.total ? (
              <p>
                Showing {talentPool?.length} of {talentMetaInfo?.total}
              </p>
            ) : null}
            {hasMoreRecords && (
              <ButtonV3
                onClick={fetchMoreData}
                color="black"
                variant="text"
                className={styles.TalentSourcingPageFooterButton}
                borderRadius={0}
              >
                Show more
              </ButtonV3>
            )}
          </div>
        </div>
      </section>
      {isOpenTalentScreeningModal ? (
        <TalentScreening
          openModal={isOpenTalentScreeningModal}
          pipeline={expandedTalentScreen || ''}
          goBack={toggleTalentScreeningMode(false)}
          viewDashboard={() => toggleTalentScreeningMode(false)}
          selectedJobStatus={'talent_pool'}
          selectedApplicationId={selectedApplicantId}
          activeFilters={filters || undefined}
          updateFilters={(filters) => setActiveFilters(filters)}
        />
      ) : null}
      {openMessageModal ? (
        <TalentMessageModal
          openMessageModal={openMessageModal}
          setOpenMessageModal={setOpenMessageModal}
          selectedCandidateIds={selectedCards}
        />
      ) : null}
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.TALENT_SOURCING, LOCALE_PAGES.HIRING],
      })),
    },
  };
}

const TalentSourcingWrapper = withAuth(TalentSourcing);

// @ts-ignore
TalentSourcingWrapper.getLayout = getLayout;

export default TalentSourcingWrapper;
